"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { fmt } from "../_lib/api";
import { getForumToken } from "./PublicAuth";
import { JoinModal, authedFetch, participationHint } from "./PublicEngage";
import { CaretDownIcon, CaretUpIcon, CommentIcon } from "./icons";

// Voting from the floor itself, Reddit-style: every feed row carries the
// up/down arrows and the score. One provider per list fetches the viewer's
// existing votes for all visible rows in a single /me/state call (rows added
// by the infinite feed register themselves), so 50 rows never mean 50 requests.

type Entry = { myVote: 1 | -1 | null; score?: number };
type FeedVotes = { get: (id: string) => Entry | undefined; register: (ids: string[]) => void };
const Ctx = createContext<FeedVotes | null>(null);

export function FeedVotesProvider({ postIds, children }: { postIds: string[]; children: React.ReactNode }) {
  const [state, setState] = useState<Record<string, Entry>>({});
  const loaded = useRef(new Set<string>());
  const idsKey = postIds.join(",");

  const load = useCallback(async (ids: string[]) => {
    if (!getForumToken()) return;
    const want = ids.filter((id) => id && !loaded.current.has(id));
    if (!want.length) return;
    want.forEach((id) => loaded.current.add(id));
    for (let i = 0; i < want.length; i += 100) {
      const chunk = want.slice(i, i + 100);
      try {
        const res = await authedFetch(`/me/state?postIds=${encodeURIComponent(chunk.join(","))}`);
        setState((s) => {
          const next = { ...s };
          for (const id of chunk) {
            const fresh = res?.posts?.find((p: any) => p.id === id);
            next[id] = { myVote: (res?.votes?.[id] as 1 | -1 | undefined) ?? null, score: typeof fresh?.score === "number" ? fresh.score : undefined };
          }
          return next;
        });
      } catch {
        chunk.forEach((id) => loaded.current.delete(id)); // retry on a later register
      }
    }
  }, []);

  useEffect(() => {
    load(idsKey ? idsKey.split(",") : []);
  }, [idsKey, load]);

  const value = useMemo<FeedVotes>(() => ({ get: (id) => state[id], register: (ids) => void load(ids) }), [state, load]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFeedVotes(): FeedVotes | null {
  return useContext(Ctx);
}

export function FeedVote({ postId, score: initialScore, commentCount }: { postId: string; score: number; commentCount: number }) {
  const feed = useFeedVotes();
  const entry = feed?.get(postId);
  const [score, setScore] = useState(initialScore);
  const [myVote, setMyVote] = useState<1 | -1 | null>(null);
  const [busy, setBusy] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!entry) return;
    setMyVote(entry.myVote);
    if (typeof entry.score === "number") setScore(entry.score);
  }, [entry?.myVote, entry?.score]); // eslint-disable-line react-hooks/exhaustive-deps

  const cast = async (dir: 1 | -1) => {
    if (!getForumToken()) {
      setShowJoin(true);
      return;
    }
    if (busy) return;
    setBusy(true);
    const prev = myVote ?? 0;
    const next = prev === dir ? 0 : dir;
    setMyVote((next || null) as 1 | -1 | null);
    setScore((s) => s - prev + next);
    try {
      const fresh = await authedFetch("/votes", { method: "PUT", body: JSON.stringify({ targetType: "post", targetId: postId, value: next }) });
      setScore(fresh.score);
      setMyVote(fresh.myVote);
    } catch (err) {
      setMyVote((prev || null) as 1 | -1 | null);
      setScore((s) => s + prev - next);
      setNotice(participationHint(err) || (err as Error).message || "That vote didn't go through.");
    } finally {
      setBusy(false);
    }
  };

  // The row is one big link; the arrows sit above it (relative z-10) and opt
  // out of the mobile tap-target inflation so the gutter stays compact.
  const btn = "fp-inline-flex relative z-10 flex h-7 w-7 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50";
  return (
    <div className="flex select-none flex-col items-center">
      {showJoin && <JoinModal onClose={() => setShowJoin(false)} />}
      {notice && (
        <div className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 shadow-lg" role="alert">
          <p>{notice}</p>
          <button onClick={() => setNotice(null)} className="mt-1.5 text-[12px] font-semibold underline underline-offset-2">
            Dismiss
          </button>
        </div>
      )}
      <button
        type="button"
        aria-label="Upvote"
        aria-pressed={myVote === 1}
        onClick={() => cast(1)}
        className={`${btn} ${myVote === 1 ? "bg-blue-50 text-blue-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-800"}`}
      >
        <CaretUpIcon />
      </button>
      <span className={`fp-mono text-[14px] font-semibold leading-none tabular-nums ${myVote === 1 ? "text-blue-700" : myVote === -1 ? "text-red-600" : "text-slate-900"}`}>
        {fmt(score)}
      </span>
      <button
        type="button"
        aria-label="Downvote"
        aria-pressed={myVote === -1}
        onClick={() => cast(-1)}
        className={`${btn} ${myVote === -1 ? "bg-red-50 text-red-600" : "text-slate-400 hover:bg-red-50 hover:text-red-600"}`}
      >
        <CaretDownIcon />
      </button>
      <span className="mt-1 inline-flex items-center gap-1 text-[11px] leading-none tabular-nums text-slate-400">
        <CommentIcon className="h-3 w-3" />
        {fmt(commentCount)}
      </span>
    </div>
  );
}
